import { getHaveOrders } from 'dux/user/selectors';
import { createSelector } from 'reselect';
import get from 'lodash/fp/get';
import getOr from 'lodash/fp/getOr';
import isNil from 'lodash/fp/isNil';
import includes from 'lodash/fp/includes';
import isEqual from 'lodash/fp/isEqual';

import * as userSelectors from 'dux/user/selectors';

import initialState from './initialState';
import * as flagFetchStatuses from './constants/flagFetchStatus';
import * as flagVariants from './constants/flagVariants';

// Get state.featureFlags
const getFeatureFlagsSubState = getOr(initialState, 'featureFlags');
// Get state.featureFlags.flags
const getFlags = createSelector(getFeatureFlagsSubState, get('flags'));

// Get state.featureFlags.flagFetchStatus
const getFlagsFetchStatus = createSelector(getFeatureFlagsSubState, get('flagFetchStatus'));
// Get state.featureFlags.flagFetchStatus === USER_CONTEXT_SPECIFIC_FAIL || USER_CONTEXT_SPECIFIC_SUCCESS
export const isFlagsDoneFetching = createSelector(
  getFlagsFetchStatus,
  flagFetchStatus =>
    flagFetchStatus === flagFetchStatuses.USER_CONTEXT_SPECIFIC_FAIL ||
    flagFetchStatus === flagFetchStatuses.USER_CONTEXT_SPECIFIC_SUCCESS
);

// Get state.featureFlags.flags.powder-challenge-closed
const getPowderChallengeClosedFlag = createSelector(getFlags, get('powder-challenge-closed'));
// Get state.featureFlags.flags.powder-challenge-closed.active
const isPowderChallengeClosedFlagActive = createSelector(
  getPowderChallengeClosedFlag,
  get('active')
);
// Get state.featureFlags.flags.powder-challenge-closed.variant
const getPowderChallengeClosedFlagVariant = createSelector(
  getPowderChallengeClosedFlag,
  get('variant')
);
// Compute derived state, returns `variant` if set, else returns `active`
const getActiveOrVariantForPowderChallengeClosed = createSelector(
  isPowderChallengeClosedFlagActive,
  getPowderChallengeClosedFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowPowderChallengeClosed = createSelector(
  getActiveOrVariantForPowderChallengeClosed,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);

// Get state.featureFlags.flags.powder-challenge-finished
const getPowderChallengeFinishedFlag = createSelector(getFlags, get('powder-challenge-finished'));
// Get state.featureFlags.flags.powder-challenge-finished.active
const isPowderChallengeFinishedFlagActive = createSelector(
  getPowderChallengeFinishedFlag,
  get('active')
);
// Get state.featureFlags.flags.powder-challenge-finished.variant
const getPowderChallengeFinishedFlagVariant = createSelector(
  getPowderChallengeFinishedFlag,
  get('variant')
);
// Compute derived state, returns `variant` if set, else returns `active`
const getActiveOrVariantForPowderChallengeFinished = createSelector(
  isPowderChallengeFinishedFlagActive,
  getPowderChallengeFinishedFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowPowderChallengeFinished = createSelector(
  getActiveOrVariantForPowderChallengeFinished,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);

// Get state.featureFlags.flags.shimmer-hair-oil
const getShimmerHairOilFlag = createSelector(getFlags, get('shimmer-hair-oil'));
// Get state.featureFlags.flags.shimmer-hair-oil.active
const isShimmerHairOilFlagActive = createSelector(getShimmerHairOilFlag, get('active'));
// Get state.featureFlags.flags.shimmer-hair-oil.variant
const getShimmerHairOilFlagVariant = createSelector(getShimmerHairOilFlag, get('variant'));
// Compute derived state, returns `variant` if set, else returns `active`
const getActiveOrVariantForShimmerHairOil = createSelector(
  isShimmerHairOilFlagActive,
  getShimmerHairOilFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowShimmerHairOil = createSelector(
  getActiveOrVariantForShimmerHairOil,
  activeOrVariant =>
    includes(activeOrVariant, ['on', 'off', 'sold-out'])
      ? activeOrVariant === 'on'
      : activeOrVariant
);

export const shouldShowShimmerHairOilRoute = createSelector(
  getActiveOrVariantForShimmerHairOil,
  activeOrVariant =>
    includes(activeOrVariant, ['on', 'off', 'sold-out'])
      ? includes(activeOrVariant, ['on', 'sold-out'])
      : activeOrVariant
);

export const getIsShimmerHairOilSoldOut = createSelector(
  getShimmerHairOilFlagVariant,
  isEqual('sold-out')
);

// Get state.featureFlags.flags.usps-delays-communication
const getUspsDelaysCommunicationFlag = createSelector(getFlags, get('usps-delays-communication'));
// Get state.featureFlags.flags.usps-delays-communication.active
const isUspsDelaysCommunicationFlagActive = createSelector(
  getUspsDelaysCommunicationFlag,
  get('active')
);
// Get state.featureFlags.flags.usps-delays-communication.variant
const getUspsDelaysCommunicationFlagVariant = createSelector(
  getUspsDelaysCommunicationFlag,
  get('variant')
);
// Compute derived state, returns `variant` if set, else returns `active`
const getActiveOrVariantForUspsDelaysCommunication = createSelector(
  isUspsDelaysCommunicationFlagActive,
  getUspsDelaysCommunicationFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowUspsDelaysCommunication = createSelector(
  getActiveOrVariantForUspsDelaysCommunication,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);

// Get state.featureFlags.flags.winback-gift
const getWinbackGiftFlag = createSelector(getFlags, get('winback-gift'));
// Get state.featureFlags.flags.winback-gift.active
const isWinbackGiftFlagActive = createSelector(getWinbackGiftFlag, get('active'));
// Get state.featureFlags.flags.winback-gift.variant
export const getWinbackGiftFlagVariant = createSelector(getWinbackGiftFlag, get('variant'));
// Compute derived state, returns `variant` if set, else returns `active`
const getActiveOrVariantForWinbackGift = createSelector(
  isWinbackGiftFlagActive,
  getWinbackGiftFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowWinbackGift = createSelector(
  getActiveOrVariantForWinbackGift,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);

// Get state.featureFlags.flags.supplements
const getSupplementsFlag = createSelector(getFlags, get('supplements'));
// Get state.featureFlags.flags.supplements.active
const isSupplementsActive = createSelector(getSupplementsFlag, get('active'));
// Get state.featureFlags.flags.supplements.variant
const getSupplementsFlagVariant = createSelector(getSupplementsFlag, get('variant'));
// Compute derived state, returns `variant` if set, else returns `active`
const getActiveOrVariantForSupplements = createSelector(
  isSupplementsActive,
  getSupplementsFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowSupplements = createSelector(
  getActiveOrVariantForSupplements,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);

// Get state.featureFlags.flags.vanity-url
const getVanityUrlFlag = createSelector(getFlags, get('vanity-url'));
// Get state.featureFlags.flags.vanity-url.active
const isVanityUrlFlagActive = createSelector(getVanityUrlFlag, get('active'));
// Get state.featureFlags.flags.vanity-url.variant
const getVanityUrlFlagVariant = createSelector(getVanityUrlFlag, get('variant'));
// Compute derived state, returns `variant` if set, else returns `active`
const getActiveOrVariantForVanityUrl = createSelector(
  isVanityUrlFlagActive,
  getVanityUrlFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowVanityUrlDailypop = createSelector(
  getActiveOrVariantForVanityUrl,
  activeOrVariant =>
    activeOrVariant === 'vanity_url_dailypop' || activeOrVariant === 'off'
      ? activeOrVariant === 'vanity_url_dailypop'
      : activeOrVariant
);
export const shouldShowVanityUrlDailypopBanner = createSelector(
  shouldShowVanityUrlDailypop,
  userSelectors.getUserReferralCode,
  (showVanityUrl, referralCode) => {
    if (!showVanityUrl || !referralCode) {
      return false;
    }
    return referralCode === 'dailypop';
  }
);

// Get state.featureFlags.flags.supplements-refill-pouches
const getSupplementsRefillPouchesFlag = createSelector(getFlags, get('supplements-refill-pouches'));
// Get state.featureFlags.flags.supplements-refill-pouches.active
const isSupplementsRefillPouchesFlagActive = createSelector(
  getSupplementsRefillPouchesFlag,
  get('active')
);
// Get state.featureFlags.flags.supplements-refill-pouches.variant
const getSupplementsRefillPouchesFlagVariant = createSelector(
  getSupplementsRefillPouchesFlag,
  get('variant')
);
// Compute derived state, returns `variant` if set, else returns `active`
const getActiveOrVariantForSupplementsRefillPouches = createSelector(
  isSupplementsRefillPouchesFlagActive,
  getSupplementsRefillPouchesFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowSupplementsRefillPouches = createSelector(
  getActiveOrVariantForSupplementsRefillPouches,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);

// Get state.featureFlags.flags.ab-test-read-more-collapsed-product-cart
const getAbTestReadMoreCollapsedProductCartFlag = createSelector(
  getFlags,
  get('ab-test-read-more-collapsed-product-cart')
);
// Get state.featureFlags.flags.ab-test-read-more-collapsed-product-cart.active
const isAbTestReadMoreCollapsedProductCartFlagActive = createSelector(
  getAbTestReadMoreCollapsedProductCartFlag,
  get('active')
);
// Get state.featureFlags.flags.ab-test-read-more-collapsed-product-cart.variant
const getAbTestReadMoreCollapsedProductCartFlagVariant = createSelector(
  getAbTestReadMoreCollapsedProductCartFlag,
  get('variant')
);
// Compute derived state, returns `variant` if set, else returns `active`
export const getActiveOrVariantForAbTestReadMoreCollapsedProductCart = createSelector(
  isAbTestReadMoreCollapsedProductCartFlagActive,
  getAbTestReadMoreCollapsedProductCartFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowAbTestReadMoreCollapsedProductCart = createSelector(
  getActiveOrVariantForAbTestReadMoreCollapsedProductCart,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);
export const isAbTestReadMoreCollapsedProductCartInExperiment = createSelector(
  getActiveOrVariantForAbTestReadMoreCollapsedProductCart,
  activeOrVariant => includes(activeOrVariant, [flagVariants.CONTROL, flagVariants.VARIANT1])
);

// Get state.featureFlags.flags.express-checkout-ab-test
const getExpressCheckoutAbTestFlag = createSelector(getFlags, get('express-checkout-ab-test'));
// Get state.featureFlags.flags.express-checkout-ab-test.active
const isExpressCheckoutAbTestFlagActive = createSelector(
  getExpressCheckoutAbTestFlag,
  get('active')
);
// Get state.featureFlags.flags.express-checkout-ab-test.variant
const getExpressCheckoutAbTestFlagVariant = createSelector(
  getExpressCheckoutAbTestFlag,
  get('variant')
);
// Compute derived state, returns `variant` if set, else returns `active`
export const getActiveOrVariantForExpressCheckoutAbTest = createSelector(
  isExpressCheckoutAbTestFlagActive,
  getExpressCheckoutAbTestFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowExpressCheckoutAbTest = createSelector(
  getActiveOrVariantForExpressCheckoutAbTest,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);
export const isExpressCheckoutAbTestInExperiment = createSelector(
  isExpressCheckoutAbTestFlagActive,
  getExpressCheckoutAbTestFlagVariant,
  (active, variant) => active && includes(variant, [flagVariants.CONTROL, flagVariants.VARIANT1])
);

// Get state.featureFlags.flags.prescription-page-ab-test
const getPrescriptionPageAbTestFlag = createSelector(getFlags, get('prescription-page-ab-test'));
// Get state.featureFlags.flags.prescription-page-ab-test.active
const isPrescriptionPageAbTestFlagActive = createSelector(
  getPrescriptionPageAbTestFlag,
  get('active')
);
// Get state.featureFlags.flags.prescription-page-ab-test.variant
const getPrescriptionPageAbTestFlagVariant = createSelector(
  getPrescriptionPageAbTestFlag,
  get('variant')
);
// Compute derived state, returns `variant` if set, else returns `active`
export const getActiveOrVariantForPrescriptionPageAbTest = createSelector(
  isPrescriptionPageAbTestFlagActive,
  getPrescriptionPageAbTestFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowPrescriptionPageAbTest = createSelector(
  getActiveOrVariantForPrescriptionPageAbTest,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);

export const isInPrescriptionPageAbTestInExperimentDraw = createSelector(
  isPrescriptionPageAbTestFlagActive,
  getPrescriptionPageAbTestFlagVariant,
  (active, variant) => active && includes(variant, [flagVariants.CONTROL, flagVariants.VARIANT1])
);

// Get state.featureFlags.flags.attentive-abc-test
const getAttentiveAbcTestFlag = createSelector(getFlags, get('attentive-abc-test'));
// Get state.featureFlags.flags.attentive-abc-test.active
const isAttentiveAbcTestFlagActive = createSelector(getAttentiveAbcTestFlag, get('active'));
// Get state.featureFlags.flags.attentive-abc-test.variant
export const getAttentiveAbcTestFlagVariant = createSelector(
  getAttentiveAbcTestFlag,
  get('variant')
);
// Compute derived state, returns `variant` if set, else returns `active`
export const getActiveOrVariantForAttentiveAbcTest = createSelector(
  isAttentiveAbcTestFlagActive,
  getAttentiveAbcTestFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowAttentiveAbcTest = createSelector(
  getActiveOrVariantForAttentiveAbcTest,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);

export const isAttentiveAbcTestInExperiment = createSelector(
  isAttentiveAbcTestFlagActive,
  getAttentiveAbcTestFlagVariant,
  (active, variant) =>
    active &&
    includes(variant, [flagVariants.CONTROL, flagVariants.VARIANT1, flagVariants.VARIANT2])
);

// Get state.featureFlags.flags.cart-redesign-ui
const getCartRedesignUIFlag = createSelector(getFlags, get('cart-redesign-ui'));
// Get state.featureFlags.flags.cart-redesign-ui.active
const isCartRedesignUIFlagActive = createSelector(getCartRedesignUIFlag, get('active'));
// Get state.featureFlags.flags.cart-redesign-ui.variant
const getCartRedesignUIFlagVariant = createSelector(getCartRedesignUIFlag, get('variant'));
// Compute derived state, returns `variant` if set, else returns `active`
export const getActiveOrVariantForCartRedesignUI = createSelector(
  isCartRedesignUIFlagActive,
  getCartRedesignUIFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowCartRedesignUI = createSelector(
  getActiveOrVariantForCartRedesignUI,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);

export const isCartRedesignUIAbTestInExperiment = createSelector(
  isCartRedesignUIFlagActive,
  getActiveOrVariantForCartRedesignUI,
  (active, variant) => active && includes(variant, [flagVariants.CONTROL, flagVariants.VARIANT1])
);
// Get state.featureFlags.flags.email-capture-exit-popup
const getEmailCaptureExitPopupFlag = createSelector(getFlags, get('email-capture-exit-popup'));
// Get state.featureFlags.flags.email-capture-exit-popup.active
const isEmailCaptureExitPopupFlagActive = createSelector(
  getEmailCaptureExitPopupFlag,
  get('active')
);
// Get state.featureFlags.flags.email-capture-exit-popup.variant
export const getEmailCaptureExitPopupFlagVariant = createSelector(
  getEmailCaptureExitPopupFlag,
  get('variant')
);
// Compute derived state, returns `variant` if set, else returns `active`
const getActiveOrVariantForEmailCaptureExitPopup = createSelector(
  isEmailCaptureExitPopupFlagActive,
  getEmailCaptureExitPopupFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowEmailCaptureExitPopup = createSelector(
  getActiveOrVariantForEmailCaptureExitPopup,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);

export const isEmailCaptureExitPopupInExperiment = createSelector(
  isEmailCaptureExitPopupFlagActive,
  getEmailCaptureExitPopupFlagVariant,
  (active, variant) => active && includes(variant, [flagVariants.CONTROL, flagVariants.VARIANT1])
);

export const isPrescriptionPageAbTestEligible = createSelector(
  getHaveOrders,
  isInPrescriptionPageAbTestInExperimentDraw,
  (haveOrders, isPrescriptionActive) => !haveOrders && isPrescriptionActive
);

export const isPrescriptionPageAbTest = createSelector(
  getHaveOrders,
  isInPrescriptionPageAbTestInExperimentDraw,
  getActiveOrVariantForPrescriptionPageAbTest,
  (haveOrders, isPrescriptionActive, prescriptionPageVariantName) =>
    !haveOrders && isPrescriptionActive && prescriptionPageVariantName === flagVariants.VARIANT1
);
// Get state.featureFlags.flags.styling-gel
const getStylingGelFlag = createSelector(getFlags, get('styling-gel'));
// Get state.featureFlags.flags.styling-gel.active
export const isStylingGelFlagActive = createSelector(getStylingGelFlag, get('active'));
// Get state.featureFlags.flags.styling-gel.variant
const getStylingGelFlagVariant = createSelector(getStylingGelFlag, get('variant'));
// Compute derived state, returns `variant` if set, else returns `active`
const getActiveOrVariantForStylingGel = createSelector(
  isStylingGelFlagActive,
  getStylingGelFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowStylingGel = createSelector(
  getActiveOrVariantForStylingGel,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);

// Get state.featureFlags.flags.free-ca-shipping
const getFreeCaShippingFlag = createSelector(getFlags, get('free-ca-shipping'));
// Get state.featureFlags.flags.free-ca-shipping.active
const isFreeCaShippingFlagActive = createSelector(getFreeCaShippingFlag, get('active'));
// Get state.featureFlags.flags.free-ca-shipping.variant
export const getFreeCaShippingFlagVariant = createSelector(getFreeCaShippingFlag, get('variant'));
// Compute derived state, returns `variant` if set, else returns `active`
export const getActiveOrVariantForFreeCaShipping = createSelector(
  isFreeCaShippingFlagActive,
  getFreeCaShippingFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowFreeCaShipping = createSelector(
  getActiveOrVariantForFreeCaShipping,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);

export const isFreeCaShippingInExperiment = createSelector(
  isFreeCaShippingFlagActive,
  getFreeCaShippingFlagVariant,
  (active, variant) =>
    active &&
    includes(variant, [flagVariants.CONTROL, flagVariants.VARIANT1, flagVariants.VARIANT2])
);

// Get state.featureFlags.flags.kustomer-livechat
const getKustomerLivechatFlag = createSelector(getFlags, get('kustomer-livechat'));
// Get state.featureFlags.flags.kustomer-livechat.active
const isKustomerLivechatFlagActive = createSelector(getKustomerLivechatFlag, get('active'));
// Get state.featureFlags.flags.kustomer-livechat.variant
const getKustomerLivechatFlagVariant = createSelector(getKustomerLivechatFlag, get('variant'));
// Compute derived state, returns `variant` if set, else returns `active`
const getActiveOrVariantForKustomerLivechat = createSelector(
  isKustomerLivechatFlagActive,
  getKustomerLivechatFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowKustomerLivechat = createSelector(
  getActiveOrVariantForKustomerLivechat,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);
// Get state.featureFlags.flags.kustomer-livechat-at-cart-ab-test
const getKustomerLivechatAtCartAbTestFlag = createSelector(
  getFlags,
  get('kustomer-livechat-at-cart-ab-test')
);
// Get state.featureFlags.flags.kustomer-livechat-at-cart-ab-test.active
const isKustomerLivechatAtCartAbTestFlagActive = createSelector(
  getKustomerLivechatAtCartAbTestFlag,
  get('active')
);
// Get state.featureFlags.flags.kustomer-livechat-at-cart-ab-test.variant
const getKustomerLivechatAtCartAbTestFlagVariant = createSelector(
  getKustomerLivechatAtCartAbTestFlag,
  get('variant')
);
// Compute derived state, returns `variant` if set, else returns `active` value
export const getActiveOrVariantForKustomerLivechatAtCartAbTest = createSelector(
  isKustomerLivechatAtCartAbTestFlagActive,
  getKustomerLivechatAtCartAbTestFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Make sure the variant is in the boundaries of the test
export const isKustomerLivechatAtCartAbTestInExperiment = createSelector(
  getActiveOrVariantForKustomerLivechatAtCartAbTest,
  activeOrVariant => includes(activeOrVariant, [flagVariants.CONTROL, flagVariants.VARIANT1])
);
// Get state.featureFlags.flags.prescription-scoring-ab-test
const getPrescriptionScoringAbTestFlag = createSelector(
  getFlags,
  get('prescription-scoring-ab-test')
);
// Get state.featureFlags.flags.prescription-scoring-ab-test.active
const isPrescriptionScoringAbTestFlagActive = createSelector(
  getPrescriptionScoringAbTestFlag,
  get('active')
);
// Get state.featureFlags.flags.prescription-scoring-ab-test.variant
const getPrescriptionScoringAbTestFlagVariant = createSelector(
  getPrescriptionScoringAbTestFlag,
  get('variant')
);
// Compute derived state, returns `variant` if set, else returns `active` value
export const getActiveOrVariantForPrescriptionScoringAbTest = createSelector(
  isPrescriptionScoringAbTestFlagActive,
  getPrescriptionScoringAbTestFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Make sure the variant is in the boundaries of the test
export const isPrescriptionScoringAbTestInExperiment = createSelector(
  getActiveOrVariantForPrescriptionScoringAbTest,
  getHaveOrders, // only for new users ie users with no orders
  (activeOrVariant, haveOrders) =>
    !haveOrders && includes(activeOrVariant, [flagVariants.CONTROL, flagVariants.VARIANT1])
);

export const isPrescriptionScoringVariantTest = createSelector(
  isPrescriptionScoringAbTestInExperiment,
  getActiveOrVariantForPrescriptionScoringAbTest,
  (isPrescriptionScoringActive, prescriptionScoringVariant) =>
    isPrescriptionScoringActive && prescriptionScoringVariant === flagVariants.VARIANT1
);
// Get state.featureFlags.flags.interstitial-screen-ab-test
const getInterstitialScreenAbTestFlag = createSelector(
  getFlags,
  get('interstitial-screen-ab-test')
);
// Get state.featureFlags.flags.interstitial-screen-ab-test.active
const isInterstitialScreenAbTestFlagActive = createSelector(
  getInterstitialScreenAbTestFlag,
  get('active')
);
// Get state.featureFlags.flags.interstitial-screen-ab-test.variant
const getInterstitialScreenAbTestFlagVariant = createSelector(
  getInterstitialScreenAbTestFlag,
  get('variant')
);
// Compute derived state, returns `variant` if set, else returns `active` value
export const getActiveOrVariantForInterstitialScreenAbTest = createSelector(
  isInterstitialScreenAbTestFlagActive,
  getInterstitialScreenAbTestFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Make sure the variant is in the boundaries of the test
export const isInterstitialScreenAbTestInExperiment = createSelector(
  getActiveOrVariantForInterstitialScreenAbTest,
  activeOrVariant => includes(activeOrVariant, [flagVariants.CONTROL, flagVariants.VARIANT1])
);

// Get state.featureFlags.flags.sunset-agave-brush
const getSunsetAgaveBrushFlag = createSelector(getFlags, get('sunset-agave-brush'));
// Get state.featureFlags.flags.sunset-agave-brush.active
const isSunsetAgaveBrushFlagActive = createSelector(getSunsetAgaveBrushFlag, get('active'));
// Get state.featureFlags.flags.sunset-agave-brush.variant
const getSunsetAgaveBrushFlagVariant = createSelector(getSunsetAgaveBrushFlag, get('variant'));
// Compute derived state, returns `variant` if set, else returns `active`
const getActiveOrVariantForSunsetAgaveBrush = createSelector(
  isSunsetAgaveBrushFlagActive,
  getSunsetAgaveBrushFlagVariant,
  (active, variant) => (active && !isNil(variant) ? variant : active)
);
// Compute derived state to be used as React Props, returns `variant` if set and not `on`/`off`, else returns `active` boolean
export const shouldShowSunsetAgaveBrush = createSelector(
  getActiveOrVariantForSunsetAgaveBrush,
  activeOrVariant =>
    activeOrVariant === 'on' || activeOrVariant === 'off'
      ? activeOrVariant === 'on'
      : activeOrVariant
);
